import { Injectable, computed, inject, signal } from "@angular/core";
import { AlertService } from "@ui";
import { forkJoin, of } from "rxjs";
import type { Corpus } from "@pienso/ingest-types/corpus.interface";
import type { Pif } from "@pienso/ingest-types/pif.interface";
import { CorpusDataService } from "../services/corpus-data.service";
import { PifDataService } from "../services/pif-data.service";

export type IngestModalState = "upload" | "refine" | "format" | "action";

// Upload is a launcher with nothing to share, so it has no URL
export type IngestModalUrlStep = Exclude<IngestModalState, "upload">;

/** What the URL records about the open modal; ids are absent for steps that don't use them. */
export interface IngestModalUrlState {
  step: IngestModalUrlStep;
  corpusId?: number;
  pifId?: number;
}

// Drives the single, app-wide ingest modal (mounted once in IngestComponent) so any component
// can open it directly to a given step by injecting this service, the same way MatDialog/CDK
// Dialog work, instead of projecting a trigger button into the modal component itself.
@Injectable({ providedIn: "root" })
export class IngestModalService {
  #corpusService = inject(CorpusDataService);
  #pifService = inject(PifDataService);
  #alertService = inject(AlertService);

  // undefined state means the modal is closed
  readonly state = signal<IngestModalState | undefined>(undefined);
  readonly corpus = signal<Corpus | undefined>(undefined);
  readonly pif = signal<Pif | undefined>(undefined);

  // Shareable URL state: null when closed, undefined while the corpus/pif for the step are still
  // loading (so the URL isn't rewritten with partial data).
  readonly urlState = computed<IngestModalUrlState | null | undefined>(() => {
    const step = this.state();
    if (!step || step === "upload") {
      return null;
    }
    const corpus = this.corpus();
    const pif = this.pif();
    if (!corpus || (step === "action" && !pif)) {
      return undefined;
    }
    return { step, corpusId: corpus.id, pifId: pif?.id };
  });

  openUpload() {
    this.corpus.set(undefined);
    this.pif.set(undefined);
    this.state.set("upload");
  }

  openRefine(corpusId: number) {
    this.corpus.set(undefined);
    this.pif.set(undefined);
    this.state.set("refine");
    this.#corpusService.getById(corpusId).subscribe({
      next: (corpus) => {
        this.corpus.set(corpus);
      },
      error: (e) => this.#handleLoadError(e),
    });
  }

  // pifId is optional: pass it to edit an existing pif's mapping, omit it to start a new one.
  // corpus/pif are set together once both requests resolve, since the format step only mounts
  // off the corpus signal - if pif resolved later it would arrive too late for the pifId input.
  openFormat(corpusId: number, pifId?: number) {
    this.corpus.set(undefined);
    this.pif.set(undefined);
    this.state.set("format");
    forkJoin([
      this.#corpusService.getById(corpusId),
      pifId ? this.#pifService.getById(pifId) : of(undefined),
    ]).subscribe({
      next: ([corpus, pif]) => {
        this.corpus.set(corpus);
        this.pif.set(pif);
      },
      error: (e) => this.#handleLoadError(e),
    });
  }

  openAction(pifId: number) {
    this.pif.set(undefined);
    this.state.set("action");
    this.#pifService.getById(pifId).subscribe({
      next: (pif) => {
        // opening straight to this step (e.g. from the data library) skips the corpus-loading steps
        if (this.corpus()?.id !== pif.corpusId) {
          this.corpus.set(undefined);
          this.#corpusService.getById(pif.corpusId).subscribe({
            next: (corpus) => {
              this.corpus.set(corpus);
            },
            error: (e) => this.#handleLoadError(e),
          });
        }
        this.pif.set(pif);
      },
      error: (e) => this.#handleLoadError(e),
    });
  }

  // Opens the step recorded in the URL state. Returns false when it doesn't describe a valid step,
  // leaving the modal untouched.
  openFromUrlState({ step, corpusId, pifId }: Partial<IngestModalUrlState>): boolean {
    if (step === "refine" && this.isId(corpusId)) {
      this.openRefine(corpusId);
    } else if (step === "format" && this.isId(corpusId)) {
      this.openFormat(corpusId, this.isId(pifId) ? pifId : undefined);
    } else if (step === "action" && this.isId(pifId)) {
      this.openAction(pifId);
    } else {
      return false;
    }
    return true;
  }

  close() {
    this.state.set(undefined);
    this.corpus.set(undefined);
    this.pif.set(undefined);
  }

  // Surfaces the failure and closes the modal instead of leaving it open on a blank step with no
  // corpus/pif to render and no way for the user to tell what went wrong.
  #handleLoadError(e: unknown) {
    this.#alertService.error(e);
    this.close();
  }

  private isId = (v: unknown): v is number => Number.isInteger(v) && (v as number) > 0;
}
